import React, { useState, useEffect, useRef } from 'react';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { colors } from '../lib/colors';
import BookingWidget from '../components/BookingWidget';
import { sendEmail } from '../services/emailService';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
    message: false
  });
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [showBookingWidget, setShowBookingWidget] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false); 
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  
  const emailRef = useRef<HTMLAnchorElement>(null);

  // Prevent scrolling when modal is open
  useEffect(() => {
    if (showBookingWidget) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showBookingWidget]);

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copiedEmail) {
      const timer = setTimeout(() => {
        setCopiedEmail(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedEmail]);

  const gradientStyle = {
    background: `radial-gradient(circle at center, ${colors.premium.dark} 0%, ${colors.premium.darker} 100%)`
  };

  const validateForm = () => {
    // Reset all errors
    setFormErrors({
      name: false,
      email: false,
      message: false
    });
    
    let isValid = true;
    const newErrors = {
      name: false,
      email: false,
      message: false
    };
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = true;
      isValid = false;
    }
    
    // Validate email
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = true;
      isValid = false;
    }
    
    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = true;
      isValid = false;
    }
    
    setFormErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    // Validate form before submission
    if (!validateForm()) {
      setError("Please fill out all required fields.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await sendEmail({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        services: selectedServices,
        message: formData.message
      });
      
      if (result.success) {
        // Reset form state
        setFormData({ name: "", email: "", phone: "", message: "" });
        setSelectedServices([]);
        setFormSubmitted(true);
      } else {
        setError(result.error || "There was a problem sending your message. Please try again or contact us directly.");
      }
    } catch (err) {
      if (import.meta.env.DEV) {

        if (import.meta.env.DEV) {
          console.error("Error submitting form:", err);
        }

      }
      const errorMessage = err instanceof Error ? err.message : "There was a problem sending your message. Please try again or contact us directly.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    if (emailRef.current) {
      navigator.clipboard.writeText(emailRef.current.textContent || 'info@pcmfinancial.ca');
      setCopiedEmail(true);
    }
  };

  const toggleService = (service: string) => {
    setSelectedServices(prev => 
      prev.includes(service)
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const services = [
    "Life Insurance",
    "Investments",
    "Succession Planning",
    "Capital Gains Planning",
    "Estate Planning",
    "Tax Planning",
    "Other"
  ];

  return (
    <MarketingLayout>
      {/* Modal Overlay */}
      {showBookingWidget && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center backdrop-blur-sm"
          onClick={() => setShowBookingWidget(false)}
        >
          {/* Modal Content */}
          <div
            className="bg-white rounded-lg w-full max-w-[95vw] md:max-w-4xl lg:max-w-5xl animate-fadeIn mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: '90vh',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}
          >
            {/* Modal Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800">Book Your Consultation</h3>
              <button
                onClick={() => setShowBookingWidget(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-0 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 60px)' }}>
              <BookingWidget />
            </div>
          </div>
        </div>
      )}

      <section className="min-h-screen" style={gradientStyle} data-component-name="Contact">
        <div className="max-w-5xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12">
            {/* Left Column - Content */}
            <div className="mb-10 lg:mb-0">
              <div className="bg-premium-darker/50 backdrop-blur-sm p-6 rounded-xl border border-premium-light/10 shadow-lg">
                <h1 className="text-2xl md:text-3xl font-bold mb-4 text-white">
                  Contact Us
                </h1>
                <p className="text-premium-light mb-6 text-base leading-relaxed">
                  We're here to answer your questions about insurance strategies and financial planning. 
                  Whether you prefer a consultation or sending a message, our team is ready to provide 
                  the information you need.
                </p>

                {/* Button to open booking modal */}
                <div className="mb-6">
                  <div className="relative inline-block">
                    <button
                      className="btn-primary text-sm px-5 py-2 rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg flex items-center"
                      onClick={() => setShowBookingWidget(true)}
                      title="Pick a time"
                    >
                      Schedule a Consultation
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2 opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                  <div className="mt-3 text-premium-light text-sm">or use the contact form →</div>
                </div>

                {/* Additional contact info */}
                <div className="mt-8 pt-6 border-t border-premium-light/10">
                  <div className="flex items-center mb-3 group">
                    <div className="h-8 w-8 bg-premium-light/10 rounded-full flex items-center justify-center mr-3 group-hover:bg-premium-light/20 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-premium-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex items-center">
                      <a 
                        href="mailto:info@pcmfinancial.ca" 
                        ref={emailRef}
                        className="text-premium-accent hover:text-premium-light transition-colors text-sm font-medium"
                      >
                        info@pcmfinancial.ca
                      </a>
                      <button 
                        onClick={copyToClipboard} 
                        className="ml-2 text-premium-light/50 hover:text-premium-light transition-colors focus:outline-none" 
                        title="Copy Email"
                        aria-label="Copy email address"
                      >
                        {copiedEmail ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center group">
                    <div className="h-8 w-8 bg-premium-light/10 rounded-full flex items-center justify-center mr-3 group-hover:bg-premium-light/20 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-premium-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <a href="tel:3068614995" className="text-premium-accent hover:text-premium-light transition-colors text-sm font-medium">
                      306-861-4995
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div>
              <div className="bg-premium-darker/50 backdrop-blur-sm p-6 rounded-xl border border-premium-light/10 shadow-lg h-full">
                {/* Simple contact form or success message */}
                {formSubmitted ? (
                  <div className="text-center p-6 rounded-lg bg-green-900/30 border border-green-700/50 flex flex-col items-center justify-center h-full">
                    <div className="mb-3 bg-green-800/30 p-2 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-green-300 font-bold text-xl mb-2">Thank you</h3>
                    <p className="text-green-400 text-sm mb-1">Your message has been sent.</p>
                    <p className="text-green-400 text-sm">We will contact you within one business day.</p>
                    <button 
                      onClick={() => setFormSubmitted(false)}
                      className="mt-5 bg-green-800/30 hover:bg-green-800/50 text-green-300 px-4 py-1.5 rounded-lg text-sm transition-colors"
                      aria-label="Close"
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <h2 className="text-xl font-semibold mb-4 text-white">Send Us a Message</h2>
                    <div className="group">
                      <label className="block text-xs font-medium mb-1 text-premium-light group-focus-within:text-premium-accent transition-colors">Full Name</label>
                      <input
                        type="text"
                        required
                        className={`w-full p-2 rounded-lg bg-premium-input text-text-light text-sm border ${formErrors.name ? 'border-red-500' : 'border-premium-light/10'} focus:ring-1 focus:ring-premium-accent focus:border-transparent transition-all`}
                        placeholder="Your Full Name"
                        value={formData.name}
                        onChange={e => {
                          setFormData({ ...formData, name: e.target.value });
                          if (e.target.value.trim()) {
                            setFormErrors(prev => ({ ...prev, name: false }));
                          }
                        }}
                      />
                      {formErrors.name && <p className="text-red-400 text-xs mt-1">Name is required</p>}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="group">
                        <label className="block text-xs font-medium mb-1 text-premium-light group-focus-within:text-premium-accent transition-colors">Email Address</label>
                        <input
                          type="email"
                          required
                          className={`w-full p-2 rounded-lg bg-premium-input text-text-light text-sm border ${formErrors.email ? 'border-red-500' : 'border-premium-light/10'} focus:ring-1 focus:ring-premium-accent focus:border-transparent transition-all`}
                          placeholder="you@email.com"
                          value={formData.email}
                          onChange={e => {
                            setFormData({ ...formData, email: e.target.value });
                            if (e.target.value.trim() && /^\S+@\S+\.\S+$/.test(e.target.value)) {
                              setFormErrors(prev => ({ ...prev, email: false }));
                            }
                          }}
                        />
                        {formErrors.email && <p className="text-red-400 text-xs mt-1">Valid email is required</p>}
                      </div>
                      
                      <div className="group">
                        <label className="block text-xs font-medium mb-1 text-premium-light group-focus-within:text-premium-accent transition-colors">Phone Number</label>
                        <input
                          type="tel"
                          className="w-full p-2 rounded-lg bg-premium-input text-text-light text-sm border border-premium-light/10 focus:ring-1 focus:ring-premium-accent focus:border-transparent transition-all"
                          placeholder="(000) 000-0000"
                          value={formData.phone}
                          onChange={e => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div className="group">
                      <label className="block text-xs font-medium mb-1 text-premium-light">What can we help you with? (Select all that apply)</label>
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        {services.map(service => (
                          <div key={service} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`service-${service.replace(/\s+/g, '-').toLowerCase()}`}
                              checked={selectedServices.includes(service)}
                              onChange={() => toggleService(service)}
                              className="mr-2 h-4 w-4 text-premium-accent rounded focus:ring-premium-accent border-premium-light/30 bg-premium-input"
                            />
                            <label 
                              htmlFor={`service-${service.replace(/\s+/g, '-').toLowerCase()}`}
                              className="text-sm text-premium-light cursor-pointer"
                            >
                              {service}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="group">
                      <label className="block text-xs font-medium mb-1 text-premium-light group-focus-within:text-premium-accent transition-colors">Message</label>
                      <textarea
                        required
                        className={`w-full p-2 rounded-lg bg-premium-input text-text-light text-sm border ${formErrors.message ? 'border-red-500' : 'border-premium-light/10'} focus:ring-1 focus:ring-premium-accent focus:border-transparent transition-all`}
                        placeholder="Let us know how we can help..."
                        rows={4}
                        value={formData.message}
                        onChange={e => {
                          setFormData({ ...formData, message: e.target.value });
                          if (e.target.value.trim()) {
                            setFormErrors(prev => ({ ...prev, message: false }));
                          }
                        }}
                      />
                      {formErrors.message && <p className="text-red-400 text-xs mt-1">Message is required</p>}
                    </div>
                    
                    {error && (
                      <div className="bg-red-900/30 text-red-400 p-3 rounded-lg border border-red-900/50 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary text-sm px-4 py-2 rounded-lg font-medium transition-all hover:scale-105 hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </div>
                      ) : (
                        "Send Message"
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
